import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountChip } from '../account/AccountChip';
import { useCurrentUser } from '../account/useAuth';
import { Button } from '../components/Button/Button';
import { AppealsView } from './AppealsView';
import { SubmissionDetail } from './SubmissionDetail';
import type { ModeratorSubmissionRow } from './moderatorApi';
import { useModeratorSubmissions } from './useModerator';
import './ModeratorDashboard.css';

const STATUS_TABS: Array<{
  value: ModeratorSubmissionRow['status'] | undefined;
  label: string;
}> = [
  { value: undefined, label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'approved', label: 'Approved' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

/**
 * `/admin/moderator` — list view. Standard / signed-out users see a
 * 403 fall-back; moderators + admins see the queue.
 *
 * The route also handles `/admin/moderator/:submissionId` via React
 * Router's nested params — the same component handles both states
 * because the queue + detail pane sit side-by-side on desktop.
 */
export function ModeratorDashboard(): JSX.Element {
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId?: string }>();
  const { data: user, isPending: userPending } = useCurrentUser();
  const [statusFilter, setStatusFilter] = useState<ModeratorSubmissionRow['status'] | undefined>(
    undefined,
  );
  const [section, setSection] = useState<'queue' | 'appeals'>('queue');

  // Wait until we know who's signed in before deciding whether to
  // 403 — avoids a flash of "not allowed" for moderators.
  if (userPending) return <p className="bl-mod__status">Loading…</p>;

  if (!user || (user.role !== 'moderator' && user.role !== 'admin')) {
    return (
      <div className="bl-mod">
        <header className="bl-mod__header">
          <button type="button" className="bl-mod__back" onClick={() => navigate('/studio')}>
            ← Back to studio
          </button>
          <AccountChip />
        </header>
        <section className="bl-mod__forbidden" role="alert">
          <h1>This area is for moderators only.</h1>
          <p>If you think you should be able to see this, ask an admin to bump your role.</p>
          <Button onClick={() => navigate('/studio')}>Open the studio</Button>
        </section>
      </div>
    );
  }

  return (
    <div className="bl-mod">
      <header className="bl-mod__header">
        <button type="button" className="bl-mod__back" onClick={() => navigate('/studio')}>
          ← Back to studio
        </button>
        <h1 className="bl-mod__title">Moderator queue</h1>
        <AccountChip />
      </header>

      <nav className="bl-mod__tabs" aria-label="Dashboard section">
        <button
          type="button"
          className={section === 'queue' ? 'bl-mod__tab bl-mod__tab--active' : 'bl-mod__tab'}
          aria-pressed={section === 'queue'}
          onClick={() => setSection('queue')}
        >
          Queue
        </button>
        <button
          type="button"
          className={section === 'appeals' ? 'bl-mod__tab bl-mod__tab--active' : 'bl-mod__tab'}
          aria-pressed={section === 'appeals'}
          onClick={() => setSection('appeals')}
        >
          Appeals
        </button>
      </nav>

      {section === 'appeals' ? (
        <AppealsView role={user.role as 'moderator' | 'admin'} />
      ) : (
        <QueueSection
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          submissionId={submissionId ?? null}
          onSelect={(id) => navigate(`/admin/moderator/${id}`, { replace: false })}
        />
      )}
    </div>
  );
}

interface QueueSectionProps {
  statusFilter: ModeratorSubmissionRow['status'] | undefined;
  onStatusFilter: (s: ModeratorSubmissionRow['status'] | undefined) => void;
  submissionId: string | null;
  onSelect: (id: string) => void;
}

function QueueSection({
  statusFilter,
  onStatusFilter,
  submissionId,
  onSelect,
}: QueueSectionProps): JSX.Element {
  return (
    <>
      <nav className="bl-mod__tabs" aria-label="Submission status filter">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            className={
              statusFilter === tab.value ? 'bl-mod__tab bl-mod__tab--active' : 'bl-mod__tab'
            }
            aria-pressed={statusFilter === tab.value}
            onClick={() => onStatusFilter(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="bl-mod__split">
        <SubmissionList
          statusFilter={statusFilter}
          activeId={submissionId ?? null}
          onSelect={onSelect}
        />
        <div className="bl-mod__detail">
          {submissionId ? (
            <SubmissionDetail submissionId={submissionId} />
          ) : (
            <p className="bl-mod__detail-empty">
              Pick a submission from the list to review the audit chain, blobs, and reports.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

interface SubmissionListProps {
  statusFilter: ModeratorSubmissionRow['status'] | undefined;
  activeId: string | null;
  onSelect: (id: string) => void;
}

function SubmissionList({ statusFilter, activeId, onSelect }: SubmissionListProps): JSX.Element {
  const { data, isPending, error } = useModeratorSubmissions(statusFilter);

  if (isPending) return <p className="bl-mod__status">Loading…</p>;
  if (error)
    return (
      <p className="bl-mod__error" role="alert">
        {error.message}
      </p>
    );
  if (!data || data.length === 0) {
    return <p className="bl-mod__empty">Nothing in this bucket. Try a different filter.</p>;
  }

  return (
    <ul className="bl-mod__list" aria-label="Submissions">
      {data.map((row) => (
        <li
          key={row.id}
          className={
            row.id === activeId
              ? 'bl-mod__list-item bl-mod__list-item--active'
              : 'bl-mod__list-item'
          }
        >
          <button type="button" className="bl-mod__list-button" onClick={() => onSelect(row.id)}>
            <span className="bl-mod__list-name">{row.project_name}</span>
            <span className="bl-mod__list-meta">
              <span
                className={`bl-mod__badge bl-mod__badge--${row.status}`}
                aria-label={`Status: ${row.status}`}
              >
                {row.status}
              </span>
              <span className="bl-mod__list-kind">{row.project_kind}</span>
              <span className="bl-mod__list-email">{row.submitter_email}</span>
            </span>
            <span className="bl-mod__list-date">{new Date(row.updated_at).toLocaleString()}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
